
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { useState } from 'react';
import {
  Modal,
  Button,
  Title,
  Paragraph,
  Flex,
  FlexItem,
  FlexJustify,
  Size,
  Tier,
  Category,
  Margin,
  Text,
} from '@drawbotics/react-drylus';
import Playground, { CodeBox } from '~/components/Playground';
export const ModalController = ({
  component: Component
}) => {
  const [visible, setVisibility] = useState(false);
  return <div>
      <Button onClick={() => setVisibility(true)} mdxType="Button">Show modal</Button>
      {React.cloneElement(Component, {
      visible,
      onClickClose: () => setVisibility(false)
    })}
    </div>;
};
export const modalMarkup = `
<div data-modal="1" class="Drylus-Modal__overlay" style="display: none">
  <div class="Drylus-Modal__container" data-element="container">
    <div class="Drylus-Modal__root">
      <div class="Drylus-Modal__close">
        <button data-element="close" class="Drylus-Button__root Drylus-Button__small Drylus-Button__round Drylus-Button__roundSmall Drylus-Button__tertiary">
          <i class="Drylus-Icon__root Drycon Drycon-x"></i>
        </button>
      </div>
      <div class="Drylus-Modal__title">
        <h4 class="Drylus-Title__root Drylus-Title__h4 Drylus-Title__noMargin">Modal title</h4>
      </div>
      <div class="Drylus-Modal__content">
        <p class="Drylus-Paragraph__root">I'm some content</p>
        <div style="height: 300px"></div>
      </div>
      <div class="Drylus-Modal__footer">
        <div class="Drylus-Flex__root Drylus-Flex__horizontal Drylus-Flex__justifyEnd Drylus-Flex__alignCenter">
          <div class="Drylus-Flex__item">
            <div class="Drylus-Margin__root Drylus-Margin__resetMargin Drylus-Margin__extraSmallRight">
              <button class="Drylus-Button__root Drylus-Button__tertiary">Secondary action</button>
            </div>
          </div>
          <div class="Drylus-Flex__item">
            <button onclick="_drylus.closeModal('1')" class="Drylus-Button__root Drylus-Button__brand">Main action</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
`;

const layoutProps = {
  ModalController,
modalMarkup
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


<h1>{`Modal`}</h1>
<p>{`This component is stateless, so you have to control its visibility state. The default behaviour is that modals are centered with the page, but if the content grows larger than the page height, then they are aligned with the top, and the whole modal scrolls.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { Modal } from '@drawbotics/react-drylus';
`}</code></pre>
<p>{`All the rendered modals are automatically attached to the end of the DOM through a `}<inlineCode parentName="p">{`modals-outlet`}</inlineCode>{`.`}</p>

<p>{`Usage:`}</p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const Example = () => {
  const [modalVisible, setModalVisibility] = useState(false);

  return (
    <div>
      <Button onClick={() => setModalVisibility(true)}>Click to show modal</Button>
      <Modal
        visible={modalVisible}
        onClickClose={() => setModalVisibility(false)}>
        <div>
          I'm some content
        </div>
      </Modal>
    </div>
  );
};
`}</code></pre>
<p>{`Basic modal example:`}</p>
<Playground __position={0} __code={`<ModalController component={
  <Modal
    visible={false}>
    <Text>I'm some content</Text>
    <div style={{ height: 500, background: 'linear-gradient(red, blue)' }} />
  </Modal>
} />`} component={Modal} mdxType="Playground">
  <ModalController component={<Modal visible={false} mdxType="Modal">
      <Text mdxType="Text">I'm some content</Text>
      <div style={{
          height: 500,
          background: 'linear-gradient(red, blue)'
        }} />
    </Modal>} mdxType="ModalController" />
</Playground>
<p>{`As you can see when the window height is smaller than the content, the modal is automatically aligned at the top and the overlay is scrollable.`}</p>
<h4>{`Complex example`}</h4>
<Playground __position={1} __code={`<ModalController component={
  <Modal
    visible={false}
    title="Modal title"
    footer={
      <Flex justify={FlexJustify.END}>
        <FlexItem>
          <Margin size={{ right: Size.EXTRA_SMALL }}>
            <Button tier={Tier.TERTIARY}>Secondary action</Button>
          </Margin>
        </FlexItem>
        <FlexItem>
          <Button category={Category.BRAND}>Main action</Button>
        </FlexItem>
      </Flex>
    }>
    <Paragraph>I'm some content</Paragraph>
    <div style={{ height: 500, background: 'linear-gradient(red, blue)' }} />
    <Margin />
    <div style={{ height: 500, background: 'linear-gradient(red, blue)' }} />
  </Modal>
} />`} mdxType="Playground">
  <ModalController component={<Modal visible={false} title="Modal title" footer={<Flex justify={FlexJustify.END} mdxType="Flex">
          <FlexItem mdxType="FlexItem">
            <Margin size={{
            right: Size.EXTRA_SMALL
          }} mdxType="Margin">
              <Button tier={Tier.TERTIARY} mdxType="Button">Secondary action</Button>
            </Margin>
          </FlexItem>
          <FlexItem mdxType="FlexItem">
            <Button category={Category.BRAND} mdxType="Button">Main action</Button>
          </FlexItem>
        </Flex>} mdxType="Modal">
      <Paragraph mdxType="Paragraph">I'm some content</Paragraph>
      <div style={{
          height: 500,
          background: 'linear-gradient(red, blue)'
        }} />
      <Margin mdxType="Margin" />
      <div style={{
          height: 500,
          background: 'linear-gradient(red, blue)'
        }} />
    </Modal>} mdxType="ModalController" />
</Playground>
<h3>{`Vanilla`}</h3>
<p>{`Because of the implementation of the modal in React that uses portals, it is not possible to have the vanilla equivalent out of the box, especially regarding documentation.`}</p>
<p>{`So to use the modal in vanilla here is what you have to do:`}</p>
<ol>
<li parentName="ol">{`Create your modal markup. This can be done wherever is easiest for you, the element will then be moved to the root of the page anyway. See the code snippet below to create a valid modal.`}</li>
<li parentName="ol">{`Add the `}<inlineCode parentName="li">{`data-modal`}</inlineCode>{` property to the modal root element and give it a unique value e.g. `}<inlineCode parentName="li">{`data-modal="1"`}</inlineCode></li>
<li parentName="ol">{`Set the root element of the modal to `}<inlineCode parentName="li">{`display: none`}</inlineCode>{`. This is important otherwise your modal will be visible when the page first loads.`}</li>
<li parentName="ol">{`Add the `}<inlineCode parentName="li">{`data-modal-trigger`}</inlineCode>{` property to the element that when clicked, will display the modal. The value has to match the one given to the modal.`}</li>
</ol>
<br />

<CodeBox mode="html" format type="vanilla" mdxType="CodeBox">{modalMarkup}</CodeBox>
<div dangerouslySetInnerHTML={{
      __html: modalMarkup
    }} />
<p>{`Open the vanilla modal with any element that has the `}<inlineCode parentName="p">{`data-modal-trigger`}</inlineCode>{` property with value equivalent to the `}<inlineCode parentName="p">{`data-modal`}</inlineCode>{` one.`}</p>
<Playground __position={5} __code={`<button className="Drylus-Button__root" data-modal-trigger="1">
  Click me
</button>`} mdxType="Playground">
  <button className="Drylus-Button__root" data-modal-trigger="1">
    Click me
  </button>
</Playground>
<p>{`If you want to easily change parts of the modal, you can remove either of these parts to make your modal simpler: `}<inlineCode parentName="p">{`Drylus-Modal__title`}</inlineCode>{`, `}<inlineCode parentName="p">{`Drylus-Modal__footer`}</inlineCode>{`.`}</p>
<p>{`If you need to close the modal manually (e.g. by clicking one of the actions in the footer) you can use the `}<inlineCode parentName="p">{`closeModal(id)`}</inlineCode>{` function provided through Drylus. You can use it like so:`}</p>
<pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<div onclick="_drylus.closeModal('1')">
  some trigger
</div>
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;